import { createSelector } from '@reduxjs/toolkit';
import { actionTypes } from 'actions/view';
import { createLoadingSelector } from 'reducers/ui';
import { conceptsSelector, ontologiesSelector } from 'selectors/entities';

export const getFacetAggregatesConceptAggregates = (
  viewFacetAggregates,
  viewFacetId,
  conceptId
) => {
  const { conceptVisualizations } = viewFacetAggregates[viewFacetId];
  if (conceptVisualizations[conceptId]) {
    return conceptVisualizations[conceptId];
  }
  return {};
};

export const viewFacetAggregatesLoadingSelector = createLoadingSelector([
  actionTypes.FETCH_TIME_SERIES_VISUALIZATIONS_REQUEST,
]);

export const viewFacetAggregatesSelector = (state) =>
  state.view.viewFacetAggregates;

export const getConceptAggregatesSelector = createSelector(
  viewFacetAggregatesSelector,
  (viewFacetAggregates) => (viewFacetId, conceptId) =>
    getFacetAggregatesConceptAggregates(
      viewFacetAggregates,
      viewFacetId,
      conceptId
    )
);

export const viewFacetSelector = (state) => state.view.viewFacet;
export const viewFacetIdSelector = (state) => state.view.viewFacet?.id;

export const aggregatesSelector = createSelector(
  viewFacetIdSelector,
  viewFacetAggregatesSelector,
  (viewFacetId, viewFacetAggregates) =>
    viewFacetAggregates[viewFacetId]?.timeSeriesVisualizations
);

export const wordcloudsSelector = createSelector(
  viewFacetIdSelector,
  viewFacetAggregatesSelector,
  (viewFacetId, viewFacetAggregates) =>
    viewFacetAggregates[viewFacetId]?.wordClouds
);

export const leavesConceptKpisSelector = (
  priorityFilter = false,
  ontologyId = null,
  leavesOnly = false
) =>
  createSelector(
    aggregatesSelector,
    ontologiesSelector(ontologyId),
    (aggregates, ontologies) => {
      const parentConceptIds = [];
      Object.values(ontologies).forEach((ontology) => {
        Object.values(ontology).forEach((concept) => {
          if (
            concept.parent?.id &&
            !parentConceptIds.includes(concept.parent?.id)
          ) {
            parentConceptIds.push(concept.parent?.id);
          }
        });
      });
      const data = [];

      Object.entries(aggregates?.conceptKpis || {}).forEach(
        ([aggregatesOntologyId, ontologyAggregates]) => {
          if (ontologyId && aggregatesOntologyId !== ontologyId) {
            return;
          }
          Object.entries(ontologyAggregates).forEach(
            ([conceptId, conceptAggregates]) => {
              const isParent = parentConceptIds.includes(conceptId);
              if (
                (!priorityFilter ||
                  (conceptAggregates?.[0]?.priority >= 0.1 &&
                    conceptAggregates?.[0]?.n_chunks >= 5)) &&
                (!leavesOnly || !isParent)
              ) {
                data.push({
                  isParent,
                  name:
                    ontologies?.[aggregatesOntologyId]?.[conceptId]?.name || '',
                  id: conceptId,
                  ...conceptAggregates[0],
                });
              }
            }
          );
        }
      );
      return data;
    }
  );

/**
 Select data used for exploration modes (table, treemap and graph).
 This code was moved from class component when refactoring to functional component.
 Code could be improved to reduce complexity of this selector together with hooks (useMemo, ...)
 *
 * @param {Object} state - The state of the application.
 * @returns {Function} A function that takes ontologyId and selectedConceptId as arguments and returns the data state.
 *
 * @function exploreDataSelector
 * @param {Object} conceptKpis - The KPIs for each concept.
 * @param {Object} concepts - The concepts data.
 * @param {string} ontologyId - The ID of the ontology.
 * @param {string} selectedConceptId - The ID of the selected concept.
 * @returns {Object} The data state containing mappedData, customTreemapData, customTreemapDataLeavesOnly, and optionally selectedConceptItem.
 *
 * @property {Object} mappedData - The mapped data for each concept.
 * @property {Array} customTreemapData - The custom treemap data.
 * @property {Array} customTreemapDataLeavesOnly - The custom treemap data containing only leaves.
 */
export const exploreDataSelector = createSelector(
  aggregatesSelector,
  conceptsSelector(),
  ({ conceptKpis }, concepts) =>
    (ontologyId, selectedConceptId) => {
      const parents = Object.values(concepts)
        .filter((concept) => concept.parent?.id)
        .map((concept) => concept.parent.id)
        .reduce((acc, parentId) => {
          if (!acc.includes(parentId)) {
            acc.push(parentId);
          }
          return acc;
        }, []);

      let selectedKpis = null;
      if (conceptKpis && selectedConceptId && !ontologyId) {
        selectedKpis = Object.entries(conceptKpis).flatMap(
          ([, ontologyKpis]) => ontologyKpis
        );
      } else if (
        conceptKpis &&
        concepts &&
        conceptKpis[ontologyId] &&
        Object.keys(concepts).length
      ) {
        selectedKpis = Object.entries(conceptKpis[ontologyId]);
      }

      // Mapped data - used for table and graph exploration
      const mappedData = {};
      const customTreemapData = [];
      const customTreemapDataLeavesOnly = [];
      selectedKpis?.forEach(([conceptId, ontologyConceptKpis]) => {
        const concept = concepts[conceptId];
        if (concept) {
          mappedData[conceptId] = [
            concept.name,
            concept.parent?.id ? concepts[concept.parent.id].name : 'Global',
            ontologyConceptKpis[0].n_chunks,
            ontologyConceptKpis[0].average_sentiment,
            ontologyConceptKpis[0].share_of_extracts,
            concept.level,
            conceptId,
            concept.parent?.id,
          ];
          const item = {
            id: conceptId,
            n_chunks: ontologyConceptKpis[0].n_chunks,
            share_of_extracts: ontologyConceptKpis[0].share_of_extracts,
            average_score: ontologyConceptKpis[0].average_score,
            label: concept.name,
            average_sentiment: ontologyConceptKpis[0].average_sentiment,
            parent_id: concept.parent?.id,
          };
          customTreemapData.push(item);
          if (!parents.includes(item.id)) {
            customTreemapDataLeavesOnly.push(item);
          }
        }
      });
      return {
        mappedData,
        customTreemapData,
        customTreemapDataLeavesOnly,
      };
    }
);

export const baseProductHierarchyGroupSelector = (state) =>
  state.entities.productHierarchyGroups?.[
    state.view.viewFacet?.base_product_hierarchy_group?.id
  ];
export const facetSourceGroupSelector = (state) =>
  state.entities.sourceGroups?.[state.view.viewFacet?.source_group?.id];

export const currentViewSelector = (state) => state.view.currentView;

export const ontologyTableDataSelector = (ontologyId) =>
  createSelector(
    ontologiesSelector(ontologyId),
    aggregatesSelector,
    (ontologies, aggregates) => {
      if (
        !aggregates?.conceptKpis ||
        !aggregates?.competitionConceptKpis ||
        !ontologies?.[ontologyId]
      )
        return null;
      const { competitionConceptKpis, conceptKpis } = aggregates || {};

      const data = [];
      Object.entries(ontologies[ontologyId]).forEach(([conceptId, concept]) => {
        const selectedConceptKpi = conceptKpis?.[ontologyId]?.[conceptId];
        if (selectedConceptKpi) {
          const competitionKpis =
            competitionConceptKpis.benchmark_kpis &&
            competitionConceptKpis.benchmark_kpis[ontologyId][conceptId]
              ? competitionConceptKpis.benchmark_kpis[ontologyId][conceptId][0]
              : {};

          data.push({
            conceptId: concept.id,
            name: concept.name,
            shareOfExtracts: selectedConceptKpi[0]?.share_of_extracts,
            competitionShareOfExtracts:
              competitionKpis?.competition_share_of_extracts,
            differentialSentiment:
              competitionKpis?.average_sentiment_differential,
            sentiment: selectedConceptKpi[0]?.average_sentiment,
          });
        }
      });
      return data;
    }
  );

export const fetchViewsLoadingSelector = createLoadingSelector([
  actionTypes.FETCH_VIEWS_REQUEST,
]);
